import './tabs.scss';

import { isEmpty } from 'lodash';
import {
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { formatTestId } from '../../data/utils/string.utils';
import { Badge } from '../badge/badge';

export enum TabsType {
  DEFAULT = 'default',
}

export enum TabsSize {
  DEFAULT = 'default',
  FULL = 'full',
}

export enum TabsIcon {
  NONE = 'none',
}

export type TabItem<T> = {
  key: T;
  title?: JSX.Element | string;
  icon?: TabsIcon;
  disabled?: boolean;
  count?: number;
};

type TabsProps<T> = PropsWithChildren<{
  testId?: string;
  type?: TabsType;
  size?: TabsSize;
  value: T;
  items: (TabItem<T> | undefined | false)[];
  onChange?: (value: T) => void;
}>;

export function Tabs<T>({
  testId,
  type = TabsType.DEFAULT,
  size = TabsSize.DEFAULT,
  items,
  children,
  onChange,
  ...props
}: TabsProps<T>) {
  const getTestId = useCallback(
    (testIdType: string | string[] | null = null) =>
      formatTestId('tabs', testId, testIdType),
    [testId]
  );

  const [selected, setSelected] = useState<T>();

  useEffect(() => {
    setSelected(props.value);
  }, [props.value]);

  const onHandleTab = useCallback(
    (value: TabItem<T>) => {
      setSelected(value.key);

      onChange?.(value.key);
    },
    [onChange]
  );

  const formattedItems = useMemo(
    () => items.filter(option => option).map(option => option as TabItem<T>),
    [items]
  );

  if (isEmpty(items)) return null;

  return (
    <div
      data-test-id={getTestId()}
      className={`tabs${type !== TabsType.DEFAULT ? ` tabs--${type}` : ``}`}
    >
      <div className="tabs__header">
        <ul className={size !== TabsSize.DEFAULT ? ` ${size}` : ''}>
          {formattedItems.map(option => (
            <li key={option.key as string}>
              <button
                data-test-id={getTestId(['option', option.key as string])}
                type="button"
                disabled={option.disabled}
                className={option.key === selected ? 'active' : ''}
                onClick={() => onHandleTab(option)}
              >
                {option.icon && option.icon !== TabsIcon.NONE && (
                  <i
                    data-test-id={getTestId([
                      'option',
                      option.key as string,
                      'icon',
                    ])}
                    className={`icon ${option.icon}`}
                  />
                )}

                {option.title && (
                  <span
                    data-test-id={getTestId([
                      'option',
                      option.key as string,
                      'text',
                    ])}
                  >
                    {option.title}
                  </span>
                )}

                {option.count !== undefined && option.count > 0 && (
                  <Badge value={option.count.toString()} />
                )}
              </button>
            </li>
          ))}
        </ul>
      </div>
      {children && <div className="tabs__content">{children}</div>}
    </div>
  );
}
